import {
  PnoeVo2Field,
  PnoeVo2Record,
  toNumber,
} from "../../entities/pnoeMeasurement";

export interface PnoeSeriesDefinition {
  /** The column's name in a record, and the chart's series key. */
  key: string;
  label: string;
  /** "" for a ratio, which the axis labels with the measure's name instead. */
  unit: string;
  /** Decimal places for display. The API sends full-precision decimals. */
  precision: number;
}

/**
 * Columns that are not plottable measures: the x axis, and the phase, which
 * is categorical and already drawn as background bands.
 */
const NON_MEASURE_FIELDS = new Set(["elapsed_time", "phase"]);

/**
 * "VO2" → "VO₂". The API sends plain digits; a digit straight after a letter
 * is a subscript by the usual convention for these names (VO2, VCO2, FeCO2,
 * PETO2), and nothing else in the column list ends in one.
 */
const withSubscripts = (label: string): string =>
  label.replace(/([A-Za-z])2/g, "$1₂");

/**
 * The measures the chart offers, from the column metadata the API sends with
 * the series.
 *
 * Narrowed to what this test actually recorded: speed, grade and power are
 * columns on every response but null on a metabolic test, and offering a
 * measure that can only draw an empty line is noise. Before any rows have
 * arrived every measure is offered, so the picker is never briefly empty.
 *
 * Ordered alphabetically by label: the API's order is its own, and a reader
 * looking for one measure among twenty scans a list, not a sequence.
 */
export const buildSeriesDefinitions = (
  fields: PnoeVo2Field[],
  records: PnoeVo2Record[] = []
): PnoeSeriesDefinition[] => {
  const measures = fields.filter(
    (field) => field.key && !NON_MEASURE_FIELDS.has(field.key)
  );

  const recorded = new Set<string>();
  for (const record of records) {
    if (recorded.size === measures.length) break;
    measures.forEach((field) => {
      if (!recorded.has(field.key) && record[field.key] != null) {
        recorded.add(field.key);
      }
    });
  }

  const offered = recorded.size
    ? measures.filter((field) => recorded.has(field.key))
    : measures;

  return offered
    .map((field) => ({
      key: field.key,
      label: withSubscripts(field.display_name || field.label || field.key),
      unit: field.unit ?? "",
      precision: field.display_decimal_places ?? 1,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export interface ChartPoint {
  elapsed: number;
  [seriesKey: string]: number | null;
}

/** Builds the chart rows from the VO₂ series, one point per breath. */
export const buildChartPoints = (
  vo2Records: PnoeVo2Record[],
  selectedKeys: string[]
): ChartPoint[] => {
  return vo2Records
    .map((record) => {
      const elapsed = toNumber(record.elapsed_time);
      if (elapsed === null) return null;
      const point: ChartPoint = { elapsed };
      selectedKeys.forEach((key) => {
        point[key] = toNumber(record[key]);
      });
      return point;
    })
    .filter((point): point is ChartPoint => point !== null)
    .sort((a, b) => a.elapsed - b.elapsed);
};

/**
 * Caps the point count so recharts stays responsive on a long test. Keeps every
 * nth point rather than averaging, so the curve keeps its real shape.
 */
export const decimatePoints = (
  points: ChartPoint[],
  maxPoints = 1000
): ChartPoint[] => {
  if (points.length <= maxPoints) return points;
  const step = Math.ceil(points.length / maxPoints);
  const kept = points.filter((_, index) => index % step === 0);
  // Always keep the last point so the x-axis ends where the test does.
  const last = points[points.length - 1];
  if (kept[kept.length - 1] !== last) kept.push(last);
  return kept;
};

/** `754` → `"12:34"`. Used for the x-axis ticks and the tooltip heading. */
export const formatElapsed = (seconds: number): string => {
  const whole = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(whole / 60);
  const secs = whole % 60;
  return `${minutes}:${String(secs).padStart(2, "0")}`;
};

/**
 * A measured value at its display precision.
 *
 * The API sends full-precision decimals (`433.1428`), which is noise in a
 * tooltip — each measure carries the number of places that is meaningful for it.
 */
export const formatSeriesValue = (
  value: unknown,
  series: PnoeSeriesDefinition | undefined
): string => {
  const numeric = typeof value === "number" ? value : Number(value);
  if (value === null || value === undefined || Number.isNaN(numeric)) return "—";
  return numeric.toFixed(series?.precision ?? 1);
};

/**
 * The smoothing windows the chart offers, matching the "Raw" dropdown in the
 * design (Figma node 16883:66653). Nothing selected is the raw data — hence no
 * "Raw" entry. The series is one record per breath and the backend's
 * `smoothCentered` is defined over sample counts, so the windows are in
 * breaths and say so.
 */
export const SMOOTHING_OPTIONS: Array<{
  key: string;
  label: string;
  windowSize: number;
}> = [
  { key: "5", label: "5 breaths", windowSize: 5 },
  { key: "9", label: "9 breaths", windowSize: 9 },
  { key: "15", label: "15 breaths", windowSize: 15 },
];

/**
 * Centered moving average for plotting — a port of the backend's function of
 * the same name, so both sides draw the same curve.
 *
 * Near the edges, averages only the available values. An odd window size is
 * required for exact centering.
 *
 * One addition over the backend version: a `null` (a breath with no value for
 * this measure) stays `null` at its own position and is left out of its
 * neighbours' averages, instead of poisoning the running sums. For an input
 * with no nulls the output is identical to the backend's.
 */
export function smoothCentered(
  values: readonly (number | null)[],
  windowSize: number
): (number | null)[] {
  if (!Number.isInteger(windowSize) || windowSize <= 0) {
    throw new RangeError("windowSize must be a positive integer");
  }
  if (windowSize % 2 === 0) {
    throw new RangeError("windowSize must be odd");
  }

  const half = Math.floor(windowSize / 2);
  const result = new Array<number | null>(values.length);
  const prefixSum = new Array<number>(values.length + 1).fill(0);
  const prefixCount = new Array<number>(values.length + 1).fill(0);

  for (let i = 0; i < values.length; i++) {
    const value = values[i];
    const present = typeof value === "number" && !Number.isNaN(value);
    prefixSum[i + 1] = prefixSum[i] + (present ? (value as number) : 0);
    prefixCount[i + 1] = prefixCount[i] + (present ? 1 : 0);
  }

  for (let i = 0; i < values.length; i++) {
    if (values[i] === null || values[i] === undefined) {
      result[i] = null;
      continue;
    }
    const start = Math.max(0, i - half);
    const end = Math.min(values.length - 1, i + half);
    const sum = prefixSum[end + 1] - prefixSum[start];
    const count = prefixCount[end + 1] - prefixCount[start];
    result[i] = count ? sum / count : null;
  }

  return result;
}

/**
 * Smooths every plotted series in place of the raw breaths, one
 * `smoothCentered` pass per series key. Window 1 (raw) returns the input as
 * is. Must run *before* `decimatePoints`: after it, neighbouring rows are no
 * longer neighbouring breaths.
 */
export const smoothChartPoints = (
  points: ChartPoint[],
  seriesKeys: string[],
  windowSize: number
): ChartPoint[] => {
  if (windowSize <= 1 || points.length === 0) return points;
  const smoothed = points.map((point) => ({ ...point }));
  seriesKeys.forEach((key) => {
    const values = points.map((point) =>
      typeof point[key] === "number" ? (point[key] as number) : null
    );
    smoothCentered(values, windowSize).forEach((value, index) => {
      smoothed[index][key] = value;
    });
  });
  return smoothed;
};
